import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { nav } from '@stores/nav'
import { productCurveStore } from '@stores/productCurvesStore'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import QtyNumberInput from '../../../../../../order/components/CellTemplates/components/QtyNumberInput'
import { getNumberFromLocale } from '@utils/get-number-form-locale'
import { makeStyles } from '@material-ui/core'
import { EnhancedListUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import useHierarchyItemTableContext from '@components/UI-Components/HierarchyWiseItemsTable/hooks/use-hierarchy-item-table-context'
import EnhancedProductQuantitiesUIStore from '@modules/showroom/productDetail/EnhancedProductQuantities/stores/EnhancedProductQuantitiesUIStore'
import { RollUpImg, SplitProductQtyImg } from '@resources/images'
import classNames from 'classnames'
import {
  CURVE_TYPES,
  ROLLUP_OPERATIONS_IDS,
  ROLL_UP_OPERATIONS_LIST,
  SPLIT_OPERATIONS_IDS,
  SPLIT_OPERATIONS_LIST
} from '@modules/showroom/EnhancedOrder/constants/uiConstants'
import RollupSplitActions from './RollUpAndSplitPopUp/RollupSplitActions'
import EnhancedProductQuantityColumnPopups from './EnhancedProductQuantityColumnPopups'
import { useEnhancedProductQuantity } from './useEnhancedProductQuantity'
import GradingNotes from './GradingNotes'
import TemplateCopyPasteButtons from './TemplateCopyPasteButtons'
import EnhancedQuantityGradingPopup from './EnhancedQuantityGradingPopup'
import { productConfiguration } from '@stores/productConfigurationStore'

const useStyles = makeStyles(() => ({
  productQtyWrapper: {
    width: '126px'
  },
  inputWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    position: 'relative',
    alignItems: 'center'
  },
  rollupBtnContainer: {
    position: 'absolute',
    left: -6
  },
  splitBtnContainer: {
    position: 'absolute',
    right: -6
  },
  updatedInputWrapper: {
    justifyContent: 'center'
  },
  qtyInput: {
    width: 86,
    height: 20,
    marginTop: 0,
    paddingLeft: 4
  },
  updatedQtyInput: {
    width: '80px',
    height: '20px',
    marginTop: '0px'
  },
  productQtyButtons: {
    width: '126px',
    paddingTop: '15px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  draftQty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '25px'
  },
  productQtyInputWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative'
  }
}))

const EnhancedListProductQuantity: FC<CellItem> = ({ rowData }) =>
  useObserver(function useHook() {
    const {
      uiStore: {
        dataStore: { summary },
        isOrderEditable
      }
    } = useOrderContext()
    const viewContextVal = useViewContext()
    const classes = useStyles()
    const {
      queryParams: { clientId, buyingSessionGroupId, orderType, clientType, bspId }
    } = nav
    const { hideSplitQuantity } = productConfiguration
    const productDetailsContextVal = useHierarchyItemTableContext()
    const selectedViewUiStore = bspId
      ? (productDetailsContextVal.store as EnhancedProductQuantitiesUIStore)
      : (viewContextVal.uiStore as EnhancedListUIStore<any>)

    const {
      updateProductQuantity,
      bulkUpdateDraftQuantity,
      getBookedDoors,
      getSumOfDraftQuantityOfBookedDoors,
      dataStore: { itemIndexObjectStore },
      deleteCopiedTemplate,
      copyTemplate,
      pasteTemplateQuantity,
      copiedTemplateQuantities,
      getSizes,
      splitActionsProductId,
      setSplitActionsProductId,
      rollUpActionsProductId,
      setRollUpActionsProductId
    } = selectedViewUiStore

    const { t } = useTranslation()

    useEffect(() => {
      if (rowDataId) {
        const curves = productCurveStore.getCurvesForProduct(
          rowDataId,
          clientId,
          buyingSessionGroupId
        )
        setProductCurves(curves)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData, clientId, buyingSessionGroupId])

    const {
      rowDataId,
      disableCopyTemplate,
      disablePasteFromTemplate,
      captureSize,
      disableQuantityInput,
      disableSplitingProductQty,
      disableRollUp,
      noBookedDoors,
      isDoorOrder,
      isGradingEnabledForProduct,
      cmRating,
      gradeCurves,
      gradingAttributes,
      bsgHasGradeCurves,
      highlightInputBox,
      productQuantityValue,
      showSaveCopyTemplate,
      setShowSaveCopyTemplate,
      showCopiedTemplates,
      setShowCopiedTemplates,
      isGradingPopupOpen,
      setIsGradingPopupOpen,
      setProductCurves,
      openDialog,
      handleGradingCancel,
      handleGradingApply,
      handleDialogClose,
      splitQuantityHandler,
      rollUpProductQtyHandler,
      currency,
      inputRef,
      hasSizes,
      hideSplitDownIcon
    } = useEnhancedProductQuantity({
      rowData,
      itemIndexObjectStore,
      isOrderEditable,
      orderType,
      clientType,
      summary,
      hideSplitQuantity,
      getBookedDoors,
      copiedTemplateQuantities,
      productCurveStore,
      clientId,
      buyingSessionGroupId,
      bulkUpdateDraftQuantity,
      updateProductQuantity,
      getSumOfDraftQuantityOfBookedDoors,
      getSizes
    })

    if (!rowData) return null

    return (
      <>
        <div className={classes.draftQty}>
          <div className={classes.productQtyWrapper}>
            <div
              className={classNames(classes.inputWrapper, {
                [classes.updatedInputWrapper]: isDoorOrder
              })}
            >
              {!bsgHasGradeCurves && !isGradingEnabledForProduct && !isDoorOrder && (
                <div className={classes.rollupBtnContainer}>
                  <RollupSplitActions
                    isIconButton={true}
                    iconTooltipLabel={t('order.rollUp', 'Roll Up')}
                    iconImageSrc={RollUpImg}
                    disableButton={disableRollUp}
                    actionsProductId={rollUpActionsProductId}
                    setActionProductId={setRollUpActionsProductId}
                    menuOptions={ROLL_UP_OPERATIONS_LIST}
                    isCaptureSize={!!captureSize}
                    hasSizes={hasSizes}
                    rowData={rowData}
                    onActionClick={optionId => {
                      if (disableRollUp) {
                        return
                      }
                      rollUpProductQtyHandler(
                        optionId === ROLLUP_OPERATIONS_IDS.SIZES_TO_DOORS,
                        optionId === ROLLUP_OPERATIONS_IDS.DOORS_TO_PRODUCT || !optionId
                      )
                    }}
                  />
                </div>
              )}
              <div className={classes.productQtyInputWrapper}>
                <QtyNumberInput
                  onChange={e =>
                    updateProductQuantity(
                      rowDataId,
                      getNumberFromLocale(e.target.value),
                      null
                    )
                  }
                  onKeyDown={() => null}
                  onFocus={e => {
                    const endIndex = e.target.value.length
                    e.target.setSelectionRange(endIndex, endIndex)
                  }}
                  disabled={disableQuantityInput}
                  className={classNames(classes.qtyInput, {
                    [classes.updatedQtyInput]: isDoorOrder
                  })}
                  value={productQuantityValue || 0}
                  ref={inputRef}
                  highlightInput={highlightInputBox}
                />
                <EnhancedQuantityGradingPopup
                  isGradingEnabledForProduct={isGradingEnabledForProduct}
                  isGradingPopupOpen={isGradingPopupOpen}
                  setIsGradingPopupOpen={setIsGradingPopupOpen}
                  handleGradingCancel={handleGradingCancel}
                  handleGradingApply={handleGradingApply}
                  cmRating={cmRating}
                  gradeCurves={gradeCurves}
                  gradingAttributes={gradingAttributes}
                />
              </div>
              {!isDoorOrder && !hideSplitDownIcon && (
                <div className={classes.splitBtnContainer}>
                  <RollupSplitActions
                    isIconButton={true}
                    iconTooltipLabel={t('order.splitProductQty', 'Split Product QTY')}
                    iconImageSrc={SplitProductQtyImg}
                    disableButton={disableSplitingProductQty}
                    actionsProductId={splitActionsProductId}
                    setActionProductId={setSplitActionsProductId}
                    menuOptions={SPLIT_OPERATIONS_LIST}
                    isCaptureSize={!!captureSize}
                    hasSizes={hasSizes}
                    rowData={rowData}
                    onActionClick={optionId => {
                      if (disableSplitingProductQty) {
                        return
                      }
                      splitQuantityHandler(
                        optionId === SPLIT_OPERATIONS_IDS.PRODUCTS_TO_DOORS || !optionId,
                        optionId === SPLIT_OPERATIONS_IDS.DOORS_TO_SIZES
                      )
                    }}
                  />
                </div>
              )}
            </div>
            <GradingNotes
              rowData={rowData}
              currency={currency}
              isGradingEnabledForProduct={isGradingEnabledForProduct}
              gradingAttributes={gradingAttributes}
            />
          </div>

          <div className={classes.productQtyButtons}>
            <TemplateCopyPasteButtons
              disableCopyTemplate={disableCopyTemplate}
              disablePasteFromTemplate={disablePasteFromTemplate}
              setShowSaveCopyTemplate={setShowSaveCopyTemplate}
              setShowCopiedTemplates={setShowCopiedTemplates}
            />
          </div>
          <EnhancedProductQuantityColumnPopups
            showSaveCopyTemplate={showSaveCopyTemplate}
            noBookedDoors={noBookedDoors}
            orderType={orderType}
            openDialog={openDialog?.open}
            showCopiedTemplates={showCopiedTemplates}
            copiedTemplateQuantities={copiedTemplateQuantities}
            setShowSaveCopyTemplate={setShowSaveCopyTemplate}
            handleDialogClose={handleDialogClose}
            setShowCopiedTemplates={setShowCopiedTemplates}
            deleteCopiedTemplate={deleteCopiedTemplate}
            dialogMessage={
              openDialog?.curveTypeToBeUsed === CURVE_TYPES.DOOR_CURVE
                ? t(
                    'order.messages.splitToDoorErrorMessage',
                    'Sorry, but there is no door distribution information available, please contact your showroom administrator for help.'
                  )
                : t(
                    'order.messages.splitToSizeErrorMessage',
                    'Sorry, but there is no size distribution information available, please contact your showroom administrator for help.'
                  )
            }
            onSelectTemplateItem={(selected, criteria) => {
              setShowCopiedTemplates(false)
              pasteTemplateQuantity(summary, rowDataId, selected, criteria)
            }}
            onSaveTemplate={copyTemplateMeta => {
              copyTemplate(
                rowDataId,
                copyTemplateMeta,
                summary.doorOrder
                  ? [{ id: summary.doorOrder.doorOrderId, door: summary.door }]
                  : summary.doorOrders
              )
              setShowSaveCopyTemplate(false)
            }}
          />
        </div>
      </>
    )
  })

export default memo(EnhancedListProductQuantity)
